import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import { Flex } from 'rebass';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { UserItem, UserItemAdmin } from '../../pages/users/Users';
import useMode from '../../hooks/useMode';
import useNotify from '../../hooks/useNotify';
import { impersonate } from '../../services/auth';
import { ApolloError, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql/language/ast';
import Form from '../Form/Form';
import UserLoginForm, { UserLoginInput } from './UserLoginForm';
import StatusChip from '../Status/StatusChip';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { UserStatus } from './types';
import ModalHeader from '../Modal/ModalHeader';
import { gql } from '@/modes/admin/gql';

const UPDATE_USER = gql(`
  mutation UpdateUser($userId: ID!, $input: UserInput!) {
    updateUser(userId: $userId, input: $input) {
      profileId
      email
    }
  }
`);

const UPDATE_USER_STATUS = gql(`
  mutation SetUserStatus($userId: ID!, $status: UserStatus!) {
    setUserStatus(userId: $userId, status: $status) {
      profileId
      status
    }
  }
`);

type UserEditModalProps = {
  onClose: () => void;
  user?: UserItem | UserItemAdmin;
  refetch?: () => void;
  refetchQueries?: DocumentNode[];
};

const AdminUserUpdateModal: React.FC<UserEditModalProps> = ({
  onClose,
  user,
  refetch,
  refetchQueries,
}) => {
  const [userInput, setUserInput] = useState<{
    email: string;
    password?: string;
  }>(
    user
      ? {
          email: user.email ?? '',
          password: '',
        }
      : {
          email: '',
          password: '',
        }
  );

  const [confirmPassword, setConfirmPassword] = useState('');

  const mode = useMode();

  const { showError, showSuccess, showWarning } = useNotify();

  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onError: (error) => {
      showError('Error updating user', error);
      console.error('Error updating user', error);
    },
    refetchQueries,
  });

  const onLoginChange = useCallback(
    (userLogin: UserLoginInput) => {
      setConfirmPassword(userLogin.confirmPassword);

      setUserInput((prevState: any) => ({
        ...prevState,
        email: userLogin.email,
        password: userLogin.password,
      }));
    },
    [setConfirmPassword, setUserInput]
  );

  const save = useCallback(async () => {
    if (!userInput.email) {
      showError('An email must be added to save this user');
      return;
    }
    // If Password and Confirm Password inputs do not match...
    if (userInput.password && userInput.password !== confirmPassword) {
      return;
    } else {
      if (userInput.password && userInput.password === '') {
        delete userInput.password;
      }

      await updateUser({
        variables: { userId: user?.profileId!, input: userInput },
        onCompleted: (data) => {
          showSuccess('User updated');
          refetch?.();
          onClose();
        },
      });
    }
  }, [user?.profileId, userInput, confirmPassword, updateUser]);

  const [
    setUserStatus,
    { data, loading: mutationLoading, error: mutationError },
  ] = useMutation(UPDATE_USER_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting user status', error);
    }, []),
    refetchQueries,
  });

  const activateDeactivate = () => {
    if (!user) {
      return;
    }

    const newStatus =
      user.status === UserStatus.ACTIVE
        ? UserStatus.INACTIVE
        : UserStatus.ACTIVE;

    if (
      newStatus === UserStatus.INACTIVE &&
      !confirm('Are you sure you want to deactivate this user?')
    ) {
      return;
    }

    setUserStatus({
      variables: { userId: user.profileId, status: newStatus },
      onCompleted: () => {
        if (newStatus === UserStatus.INACTIVE) {
          showWarning('User deactivated');
        } else {
          showSuccess('User activated');
        }
        refetch?.();
        onClose();
      },
    });
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title={user?.profile.name ?? undefined}>
        <StatusChip
          ml={2}
          status={user?.status === UserStatus.ACTIVE ? 'success' : 'error'}
        >
          {constantToLabel(user?.status)}
        </StatusChip>
      </ModalHeader>
      <ModalBody>
        <Form>
          {/*<FormColumn>*/}
          {/*  {labels && <Label>Name</Label>}*/}
          {/*  <Input*/}
          {/*    width="100%"*/}
          {/*    type="text"*/}
          {/*    value={profileInput.name || ''}*/}
          {/*    onChange={handleTesterProfileObjChange}*/}
          {/*    name="name"*/}
          {/*    placeholder="Name"*/}
          {/*    autoComplete="off"*/}
          {/*  />*/}
          {/*</FormColumn>*/}
          <UserLoginForm
            onChange={onLoginChange}
            value={{ ...userInput, confirmPassword }}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        {mode === 'admin' && (
          <Flex flexGrow={1} sx={{ gap: 2 }}>
            <Button
              variant="secondary"
              icon="logout"
              onClick={async () => {
                if (user) {
                  try {
                    await impersonate(user.profileId);
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }
              }}
            >
              Impersonate
            </Button>
            {user?.status === UserStatus.ACTIVE && (
              <Button
                icon="slash-circle"
                variant="danger"
                onClick={activateDeactivate}
              >
                Deactivate
              </Button>
            )}
            {user?.status === UserStatus.INACTIVE && (
              <Button variant="success" onClick={activateDeactivate}>
                Activate
              </Button>
            )}
          </Flex>
        )}
        <Flex justifyContent="flex-end" sx={{ gap: 2 }}>
          <Button icon="save" disabled={updateLoading} onClick={save}>
            Save
          </Button>
          <Button variant="outline" icon="slash-circle" onClick={onClose}>
            Cancel
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

AdminUserUpdateModal.propTypes = {
  onClose: PropTypes.any,
  user: PropTypes.any,
};
export default AdminUserUpdateModal;
